import React from 'react';
import { X, Minimize2 } from 'lucide-react';
import AgentSessionList from './AgentSessionList';
import AgentChat from './AgentChat';
import { useAgent } from '../../context/AgentContext';

/**
 * AgentFullScreen — three-panel full viewport layout.
 *
 * Props:
 *   pageContext
 *   onExitFullScreen
 *   onKillSession
 */
const AgentFullScreen = ({ pageContext, onExitFullScreen, onKillSession }) => {
  const { activeSession, createSession } = useAgent();

  const handleNewSession = () => createSession(pageContext);

  return (
    <div className="fixed inset-0 z-[9998] bg-accent agent-dot-bg flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 h-12 bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <VasariLogo />
          <span className="text-sm font-medium text-accent truncate max-w-xs">
            {activeSession?.title || 'Vasari Agent'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onExitFullScreen}
            className="p-1.5 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors"
            title="Exit full screen"
          >
            <Minimize2 size={16} />
          </button>
          <button
            onClick={onKillSession}
            className="p-1.5 text-accent/60 hover:text-accent hover:bg-blue-50 rounded transition-colors"
            title="Kill session"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Two-panel body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left rail — session history */}
        <div className="w-64 border-r border-white/20 flex-shrink-0 overflow-hidden">
          <AgentSessionList onNewSession={handleNewSession} />
        </div>

        {/* Center — conversation (full remaining width) */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <AgentChat pageContext={pageContext} />
        </div>
      </div>
    </div>
  );
};

/* SVG logo mark — blue on white variant for full screen header */
const VasariLogo = () => (
  <svg width="18" height="18" viewBox="0 0 8689 9614" fill="#1D4ED8" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="5905" y="1006" width="1935" height="3121" transform="rotate(90 5905 1006)" />
    <rect x="4792" y="0" width="2819" height="895" transform="rotate(90 4792 0)" />
    <rect x="5566" y="548" width="916" height="2445" transform="rotate(90 5566 548)" />
    <rect x="3123" y="2900" width="1935" height="3121" transform="rotate(90 3123 2900)" />
    <rect x="2010" y="1894" width="3371" height="895" transform="rotate(90 2010 1894)" />
    <rect x="2784" y="2442" width="916" height="2445" transform="rotate(90 2784 2442)" />
    <rect x="8689" y="2928" width="1935" height="3121" transform="rotate(90 8689 2928)" />
    <rect x="7576" y="1922" width="3371" height="895" transform="rotate(90 7576 1922)" />
    <rect x="8350" y="2470" width="916" height="2445" transform="rotate(90 8350 2470)" />
    <rect x="2782" y="8608" width="1935" height="3121" transform="rotate(-90 2782 8608)" />
    <rect x="3895" y="9614" width="2900" height="895" transform="rotate(-90 3895 9614)" />
    <rect x="3121" y="9066" width="916" height="2445" transform="rotate(-90 3121 9066)" />
    <rect x="5566" y="6742" width="1935" height="3121" transform="rotate(-90 5566 6742)" />
    <rect x="6679" y="7748" width="3371" height="895" transform="rotate(-90 6679 7748)" />
    <rect x="5905" y="7200" width="916" height="2445" transform="rotate(-90 5905 7200)" />
    <rect y="6714" width="1935" height="3121" transform="rotate(-90 0 6714)" />
    <rect x="1113" y="7720" width="3371" height="895" transform="rotate(-90 1113 7720)" />
    <rect x="339" y="7172" width="916" height="2445" transform="rotate(-90 339 7172)" />
  </svg>
);

export default AgentFullScreen;
